import React, { useEffect, useState } from 'react';
import { Trash2, Pencil, GripVertical, Check, X, Image as ImageIcon, Film, Box as BoxIcon, Smile, Shirt, Palette } from 'lucide-react';
import { fileUrl, deleteMedia, updateMedia, reorderMedia } from '../lib/api';
import { ActionBtn, MoveButtons, moveItem } from '../components/ReorderControls';
import ExpressionEditor from './ExpressionEditor';
import LooksEditor from './LooksEditor';
import TexturePanel, { NoTexturesBadge } from './TexturePanel';
import StyleEditor from './StyleEditor';
import ReplaceFileBtn from './ReplaceFileBtn';

const fieldCls = 'w-full bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-body text-sm px-3 py-2.5 rounded';
const canDrag = () => typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;

export default function AdminMediaList({ items, category, onChanged }) {
  const [rows, setRows] = useState(items);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [movedId, setMovedId] = useState(null);
  const [announce, setAnnounce] = useState('');
  useEffect(() => { setRows(items); }, [items]);

  const commit = async (next, id) => {
    setRows(next);
    setMovedId(id);
    setTimeout(() => setMovedId(null), 700);
    const pos = next.findIndex((r) => r.id === id);
    setAnnounce(`${next[pos].title} moved to position ${pos + 1} of ${next.length}`);
    await reorderMedia(category, next.map((r) => r.id));
    onChanged();
  };

  const move = (idx, dir) => commit(moveItem(rows, idx, dir), rows[idx].id);

  const drop = (targetId) => {
    setOverId(null);
    if (!dragId || dragId === targetId) return;
    const from = rows.findIndex((r) => r.id === dragId);
    const to = rows.findIndex((r) => r.id === targetId);
    setDragId(null);
    commit(moveItem(rows, from, to - from), rows[from].id);
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    await deleteMedia(it.id);
    onChanged();
  };

  if (!rows.length) {
    return (
      <div className="font-osd text-xs text-[#8b6a3e] border-2 border-dashed border-[#5a3418] px-4 py-10 text-center rounded-lg" data-testid="admin-media-list">
        NO BROADCASTS IN THIS CHANNEL YET.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-media-list">
      <div className="sr-only" aria-live="polite" data-testid="admin-reorder-announce">{announce}</div>
      {rows.length > 1 && (
        <div className="font-osd text-[11px] text-[#8b6a3e] tracking-widest">
          // USE ▲ ▼<span className="hidden md:inline"> OR DRAG THE GRIP</span> TO SET THE ORDER VISITORS SEE
        </div>
      )}
      {rows.map((it, i) => (
        <MediaRow key={it.id} it={it} index={i} total={rows.length} onMove={move}
                  dragging={dragId === it.id} over={overId === it.id && dragId && dragId !== it.id} moved={movedId === it.id}
                  dragHandlers={{
                    onDragStart: () => setDragId(it.id),
                    onDragEnd: () => { setDragId(null); setOverId(null); },
                    onDragOver: (e) => { e.preventDefault(); setOverId(it.id); },
                    onDrop: () => drop(it.id)
                  }}
                  onRemove={() => remove(it)} onSaved={onChanged} />
      ))}
    </div>
  );
}

function MediaRow({ it, index, total, onMove, dragging, over, moved, dragHandlers, onRemove, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [exprOpen, setExprOpen] = useState(false);
  const [looksOpen, setLooksOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [replaced, setReplaced] = useState(null);
  const draggable = !editing && !exprOpen && !looksOpen && !styleOpen && canDrag();

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; dragHandlers.onDragStart(); }}
      onDragEnd={dragHandlers.onDragEnd}
      onDragOver={dragHandlers.onDragOver}
      onDrop={(e) => { e.preventDefault(); dragHandlers.onDrop(); }}
      className={`p-3 sm:p-4 border-2 bg-[#0f0704] rounded-lg transition-[opacity,border-color] ${dragging ? 'opacity-40' : ''} ${over ? 'border-[#f5a623]' : 'border-[#3a2512]'} ${moved ? 'row-moved' : ''}`}
      data-testid={`admin-item-${it.id}`}
    >
      <div className="flex gap-3 items-start">
        {draggable && (
          <div className="hidden md:flex self-center text-[#8b6a3e] hover:text-[#f5a623] cursor-grab active:cursor-grabbing" data-testid={`admin-drag-${it.id}`} aria-hidden>
            <GripVertical className="w-5 h-5" />
          </div>
        )}
        <Thumb it={it} />
        {editing
          ? <EditForm it={it} onDone={() => { setEditing(false); onSaved(); }} onCancel={() => setEditing(false)} />
          : <Details it={it} />}
      </div>
      {!editing && (
        <div className="mt-3 pt-3 border-t border-[#2a1608] flex items-center justify-between gap-2 flex-wrap">
          <MoveButtons index={index} total={total} onMove={onMove} label={it.title}
                       upTestId={`admin-up-${it.id}`} downTestId={`admin-down-${it.id}`} />
          <div className="flex gap-1.5">
            {it.type === 'model' && <ReplaceFileBtn it={it} onSaved={onSaved} onStatus={setReplaced} />}
            {it.type === 'model' && (
              <ActionBtn onClick={() => setStyleOpen(true)} testId={`admin-style-${it.id}`} label={`Edit style for ${it.title}`}>
                <Palette className="w-4 h-4" /><span className="hidden sm:inline">STYLE</span>
              </ActionBtn>
            )}
            {it.type === 'model' && (
              <ActionBtn onClick={() => setLooksOpen(true)} testId={`admin-looks-${it.id}`} label={`Edit looks for ${it.title}`}>
                <Shirt className="w-4 h-4" /><span className="hidden sm:inline">LOOKS</span>
              </ActionBtn>
            )}
            {it.type === 'model' && (
              <ActionBtn onClick={() => setExprOpen(true)} testId={`admin-expressions-${it.id}`} label={`Edit expressions for ${it.title}`}>
                <Smile className="w-4 h-4" /><span className="hidden sm:inline">EXPRESSIONS</span>
              </ActionBtn>
            )}
            <ActionBtn onClick={() => setEditing(true)} testId={`admin-edit-${it.id}`} label={`Edit ${it.title}`}>
              <Pencil className="w-4 h-4" /><span className="hidden sm:inline">EDIT</span>
            </ActionBtn>
            <ActionBtn onClick={onRemove} testId={`admin-delete-${it.id}`} label={`Delete ${it.title}`} tone="danger">
              <Trash2 className="w-4 h-4" />
            </ActionBtn>
          </div>
        </div>
      )}
      {!editing && it.type === 'model' && <TexturePanel it={it} onSaved={onSaved} />}
      {exprOpen && <ExpressionEditor it={it} onClose={() => setExprOpen(false)} onSaved={onSaved} />}
      {replaced && (
        <div className={`mt-2 font-osd text-[11px] tracking-wider ${replaced.ok ? 'text-[#9dff7a]' : 'text-[#ff6a3a]'}`} role="status" data-testid={`admin-replace-status-${it.id}`}>{replaced.text}</div>
      )}
      {styleOpen && <StyleEditor it={it} onClose={() => setStyleOpen(false)} onSaved={onSaved} />}
      {looksOpen && <LooksEditor it={it} onClose={() => setLooksOpen(false)} onSaved={onSaved} />}
    </div>
  );
}

function Thumb({ it }) {
  const Icon = it.type === 'video' ? Film : it.type === 'model' ? BoxIcon : ImageIcon;
  const thumb = it.type === 'image' ? it.filename : it.type === 'video' ? it.poster_filename : null;
  return (
    <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded bg-[#1a0e05] border border-[#3a2512] overflow-hidden flex items-center justify-center">
      {thumb
        ? <img src={fileUrl(thumb)} alt="" className="w-full h-full object-cover" draggable={false} />
        : it.type === 'video'
          ? <video src={`${fileUrl(it.filename)}#t=0.1`} preload="metadata" muted playsInline className="w-full h-full object-cover pointer-events-none" />
          : <Icon className="w-7 h-7 text-[#f5a623]" />}
    </div>
  );
}

function Details({ it }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="font-display text-base sm:text-lg text-[#ffe9b8] leading-tight break-words">{it.title}</div>
      <div className="flex items-center gap-2 mt-1 font-osd text-[11px] tracking-widest">
        <span className="text-[#8b6a3e] uppercase">{it.type}</span>
        <span className="text-[#5a3418]">·</span>
        <span className="text-[#f5a623]">{it.date}</span>
        <NoTexturesBadge it={it} />
      </div>
      <p className="font-body text-xs text-[#a88552] mt-1.5 line-clamp-2">{it.description || <em className="opacity-60">No description.</em>}</p>
    </div>
  );
}

function EditForm({ it, onDone, onCancel }) {
  const [form, setForm] = useState({ title: it.title, date: it.date, description: it.description || '' });
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    try { await updateMedia(it.id, { ...form, title: form.title.trim() }); onDone(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="flex-1 min-w-0 space-y-2" data-testid={`admin-edit-form-${it.id}`}>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={form.title} onChange={(e) => set('title', e.target.value)} aria-label="Title" className={fieldCls} data-testid={`admin-edit-title-${it.id}`} />
        <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} aria-label="Date" className={`${fieldCls} sm:max-w-[11rem]`} data-testid={`admin-edit-date-${it.id}`} />
      </div>
      <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} aria-label="Description"
                className={`${fieldCls} resize-y`} data-testid={`admin-edit-description-${it.id}`} />
      {err && <div className="font-osd text-xs text-[#ff6a3a]" role="alert" data-testid={`admin-edit-error-${it.id}`}>{err}</div>}
      <div className="flex gap-2">
        <ActionBtn onClick={save} testId={`admin-edit-save-${it.id}`} label="Save changes" tone="accent"><Check className="w-4 h-4" /> SAVE</ActionBtn>
        <ActionBtn onClick={onCancel} testId={`admin-edit-cancel-${it.id}`} label="Cancel editing"><X className="w-4 h-4" /> CANCEL</ActionBtn>
      </div>
    </div>
  );
}
